import {
  Button,
  Card,
  Empty,
  Input,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import InstitutionCreateModal from "./components/InstitutionCreateModal";
import { getAllInstitutions } from "../../../api/institutionApi";
import { useOpenRoute } from "../../../utils/openRouteUtils";
import { getTypeColor } from "../../../utils/themeUtils";

const { Title, Text } = Typography;

export default function InstitutionsPage() {
  const openRoute = useOpenRoute();

  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const loadInstitutions = async () => {
    try {
      setLoading(true);
      const data = await getAllInstitutions();
      setInstitutions(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstitutions();
  }, []);

  const filteredData = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return institutions;

    return institutions.filter((item) => {
      const name = item?.name?.toLowerCase() || "";
      const type = item?.type?.toLowerCase() || "";
      return name.includes(q) || type.includes(q);
    });
  }, [institutions, keyword]);

  const columns = [
    {
      title: "Нэр",
      dataIndex: "name",
      key: "name",
      render: (value) => (
        <span className="text-[15px] font-semibold text-slate-800">
          {value || "-"}
        </span>
      ),
    },
    {
      title: "Төрөл",
      dataIndex: "type",
      key: "type",
      width: 180,
      render: (value) => <Tag color={getTypeColor(value)}>{value || "-"}</Tag>,
    },
    {
      title: "Төлөв",
      key: "is_active",
      width: 120,
      render: (_, record) => {
        const active = record?.is_active ?? record?.isActive;
        if (active === undefined || active === null) return null;
        return active ? (
          <Tag color="green">Идэвхтэй</Tag>
        ) : (
          <Tag color="default">Идэвхгүй</Tag>
        );
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (value) => (
        <span className="font-mono text-sm text-slate-500">{value}</span>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-5">
      <Card className="rounded-2xl shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <Title level={3} className="!mb-1">
              Байгуулгууд
            </Title>
            <Text className="text-slate-500">
              {keyword
                ? `${filteredData.length} / ${institutions.length} байгууллага`
                : `${institutions.length} байгууллага`}
            </Text>
          </div>

          <Space wrap>
            <Input
              allowClear
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              prefix={<SearchOutlined className="text-slate-400" />}
              placeholder="Нэрээр хайх"
              className="rounded-xl"
              style={{ width: 260 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={loadInstitutions}
              loading={loading}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              Байгууллага нэмэх
            </Button>
          </Space>
        </div>
      </Card>

      <Card className="rounded-2xl shadow-sm">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredData}
          pagination={{ pageSize: 100, showSizeChanger: false }}
          locale={{
            emptyText: <Empty description="Байгууллага олдсонгүй" />,
          }}
          onRow={(record) => ({
            onClick: (event) =>
              openRoute(`/admin/institutions/${record.id}`, event),
            className: "cursor-pointer",
          })}
        />
      </Card>

      <InstitutionCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={loadInstitutions}
      />
    </div>
  );
}